import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabase'
import { ACCESS_MESSAGES, loadAccessProfile, signInWithEmail } from '../services/authService'

const AuthContext = createContext(null)
const emptyAccess = { cpUsuario: null, organizacion: null, rol: null }

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    session: null, authUser: null, ...emptyAccess, loading: true, authError: '',
  })
  const hydratedUserId = useRef(null)
  const localSignIn = useRef(false)

  const applySession = useCallback(async (session) => {
    if (!session?.user) {
      hydratedUserId.current = null
      setState({ session: null, authUser: null, ...emptyAccess, loading: false, authError: '' })
      return
    }

    if (hydratedUserId.current === session.user.id) {
      setState((current) => ({ ...current, session, authUser: session.user, loading: false }))
      return
    }

    setState((current) => ({ ...current, session, authUser: session.user, loading: true }))
    try {
      const access = await loadAccessProfile(session.user)
      hydratedUserId.current = session.user.id
      setState({ session, authUser: session.user, ...access, loading: false, authError: '' })
    } catch (error) {
      hydratedUserId.current = null
      await supabase.auth.signOut().catch(() => {})
      setState({ session: null, authUser: null, ...emptyAccess, loading: false, authError: error.message })
    }
  }, [])

  useEffect(() => {
    let active = true

    if (!supabaseConfigured) {
      setState((current) => ({ ...current, loading: false }))
      return undefined
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active || (event === 'SIGNED_IN' && localSignIn.current)) return
      // INITIAL_SESSION sustituye a getSession y evita dos refresh simultáneos
      // durante el doble montaje de efectos de StrictMode.
      window.setTimeout(() => {
        if (active) applySession(session)
      }, 0)
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [applySession])

  const signIn = useCallback(async (email, password) => {
    setState((current) => ({ ...current, authError: '' }))
    localSignIn.current = true
    try {
      const access = await signInWithEmail(email, password)
      hydratedUserId.current = access.user.id
      setState({
        session: access.session,
        authUser: access.user,
        cpUsuario: access.cpUsuario,
        organizacion: access.organizacion,
        rol: access.rol,
        loading: false,
        authError: '',
      })
    } finally {
      localSignIn.current = false
    }
  }, [])

  // Recarga cp_usuarios/organización de la sesión actual (p. ej. tras el cambio de
  // contraseña inicial) sin recargar la página. Devuelve el perfil actualizado.
  const refreshProfile = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) throw new Error(ACCESS_MESSAGES.connection)
    const access = await loadAccessProfile(session.user)
    hydratedUserId.current = session.user.id
    setState((current) => ({ ...current, session, authUser: session.user, ...access, loading: false, authError: '' }))
    return access
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw new Error(ACCESS_MESSAGES.connection)
    hydratedUserId.current = null
    setState({ session: null, authUser: null, ...emptyAccess, loading: false, authError: '' })
  }, [])

  const value = {
    ...state,
    isAuthorized: Boolean(state.session && state.cpUsuario && state.organizacion),
    // Contraseña temporal: hasta cambiarla solo se permite /cambiar-clave-inicial.
    debeCambiarClave: Boolean(state.cpUsuario?.debe_cambiar_clave),
    signIn,
    signOut,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth debe utilizarse dentro de AuthProvider')
  return context
}
